/**
 * Invite a person — a colleague (Team) or into a tenant (People). saas-auth ALWAYS answers `202`
 * whether or not the address can be invited (anti-enumeration, H §5), so success is one neutral
 * toast: "Invitation sent if the address can be invited." The signed-in member's own email is
 * blocked client-side; `403 RESELLER_MEMBER_NOT_INVITABLE` is the email field's error; every
 * other rejection is an inline message.
 */
import { useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCurrentOperator } from '@/auth/authHooks';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useToast } from '@/components/useToast';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';
import { inviteEmailError, isSelfInvite, looksLikeEmail } from '@/tenant/inviteGuard';
import { useMemberError, type MemberError } from './memberErrors';
import type { RoleOption } from './RoleDialog';

const FIELDS = ['email'] as const;

export interface InviteMemberDialogProps {
  open: boolean;
  title: string;
  roles: RoleOption[];
  defaultRole: string;
  onInvite: (body: { email: string; role: string }) => Promise<void>;
  onClose: () => void;
  /** After a `202` — refetch the invitations. */
  onDone: () => void;
}

export function InviteMemberDialog(props: InviteMemberDialogProps) {
  // A fresh form on every open (TMMRSL-0071).
  return <InviteMemberForm key={useFreshOnOpen(props.open)} {...props} />;
}

function InviteMemberForm({
  open,
  title,
  roles,
  defaultRole,
  onInvite,
  onClose,
  onDone,
}: InviteMemberDialogProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const describe = useMemberError();
  const ownEmail = useCurrentOperator()?.email;
  const emailRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(defaultRole);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<MemberError | null>(null);
  const fields = useFieldErrors(FIELDS, { email: emailRef }, submitting);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!looksLikeEmail(email)) {
      fields.fail({ email: t('validation.email') });
      return;
    }
    if (isSelfInvite(email, ownEmail)) {
      fields.fail({ email: t('tenant.invite.self') });
      return;
    }
    setSubmitting(true);
    fields.reset();
    try {
      await onInvite({ email: email.trim(), role });
      showToast(t('members.toast.invited'));
      onDone();
      onClose();
    } catch (err) {
      const fieldError = inviteEmailError(err, t);
      if (fieldError) fields.fail({ email: fieldError });
      else setError(describe(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose}>
      <Box component="form" noValidate onSubmit={(event) => void handleSubmit(event)}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <TextField
            required
            fullWidth
            type="email"
            margin="dense"
            label={t('members.field.email')}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              fields.clear('email');
            }}
            inputRef={emailRef}
            error={fields.errors.email !== undefined}
            helperText={fields.errors.email}
            disabled={submitting}
          />
          <TextField
            select
            fullWidth
            margin="dense"
            label={t('members.field.role')}
            value={role}
            onChange={(event) => setRole(event.target.value)}
            disabled={submitting}
          >
            {roles.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={submitting}>
            {t('members.sendInvite')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
